<template>
  <v-select
    :model-value="modelValue"
    :items="filteredItems"
    :disabled="disabled"
    :menu-props="{ maxHeight: '300' }"
    :placeholder="$t('devices.filters.clientVersion')"
    :data-testid="dataTestId"
    density="compact"
    item-value="name"
    item-title="name"
    variant="outlined"
    rounded
    hide-details
    bg-color="white"
    :clearable="clearable"
    @update:model-value="$emit('update:modelValue', $event)"
    @update:menu="search = ''"
  >
    <template #selection="{ item }">
      <span class="body2">{{ `Coro ${item.title}` }}</span>
    </template>
    <template #prepend-item>
      <cui-select-search-field
        v-model="search"
        :placeholder="$t('general.search')"
        :data-test-id="`${dataTestId}-search`"
      />
    </template>
    <template #item="{ item, props: itemProps }">
      <v-list-subheader v-if="item.raw.subheader" :title="item.raw.name" />
      <v-list-item v-else v-bind="itemProps" :data-testid="`${dataTestId}-${item.raw.name}-item`">
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { CuiSelectSearchField, useCuiSelectSearch } from "@coronet/cui";

interface VersionItem {
  subheader?: boolean;
  name: string;
}

/**
 * Device "Coro version" (client version) single-select with an in-dropdown search.
 * Options depend on the selected OS version, so the parent resolves and passes `items`
 * (via the `clientVersionItems(osVersion)` helper). Subheader rows group the versions.
 */
export default defineComponent({
  components: { CuiSelectSearchField },
  props: {
    /** Selected client-version name (v-model), or null. */
    modelValue: {
      type: String as PropType<string | null>,
      default: null,
    },
    /** Resolved version list (subheader rows + version rows); built by the parent. */
    items: {
      type: Array as PropType<VersionItem[]>,
      default: () => [],
    },
    /** Disables the select. */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Shows the clear (x) affordance. */
    clearable: {
      type: Boolean,
      default: false,
    },
    /** `data-testid` for the select; items derive `${dataTestId}-${name}-item`, search `${dataTestId}-search`. */
    dataTestId: {
      type: String,
      default: "device-coro-version-filter",
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const { search, filteredItems } = useCuiSelectSearch<VersionItem>(
      () => props.items,
      (item) => item.name,
      { isSubheader: (item) => item.subheader === true }
    );

    return { search, filteredItems };
  },
});
</script>
